import React, { useEffect, useMemo, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography
} from '@mui/material'
import { useMutation, useQuery } from '@tanstack/react-query'
import B2bCommonApi from 'api/b2b/B2bCommonApi'
import B2bTDPAPI, { ITDPBackendData } from 'api/b2b/TdpApi'
import TechnologyCompanyApi from 'api/b2b/TechnologyCompanyApi'
import OrbitSession from 'hooks/useCookies'
import { debounce } from 'lodash'

import {
  BusinessTypes,
  CommunicationLanguage,
  ConfirmOptions,
  countryCodes,
  CurrencySupported,
  ReferralSource,
  TDPRegistrationStatus
} from '../contants'
import { TenantDetails } from '..'

export interface IAutoCompleteDropdown {
  label: string
  value: string
}

interface IFormInput {
  companyName: string
  companyLegalName: string
  registrationNumber: string
  contactPersonFirstName: string
  contactPersonLastName: string
  email: string
  phoneNumber: string
  promoteEsim: IAutoCompleteDropdown
  countryCode: IAutoCompleteDropdown | null
  country: IAutoCompleteDropdown
  city: IAutoCompleteDropdown
  status: IAutoCompleteDropdown
  rejectReason: string
  internalRemark: string
  refSource: IAutoCompleteDropdown
  monthlyVolume: string
  currencyCode: IAutoCompleteDropdown
  businessType: IAutoCompleteDropdown
  businessAddress: string
  websiteURL: string
  soldEsimCount: string
  projectedEsimCount: string
  expectedToBookRatio: string
  projectedMonthlyVolume: string
  accountManager: IAutoCompleteDropdown
  '3ptp': IAutoCompleteDropdown
  communicationLanguage: IAutoCompleteDropdown
}

interface ITDPResponse {
  data: { data: string }
}

interface TDPDetailsProps {
  onSaveSuccess: (string) => void
  setTenantDetails: (tenantDetails: TenantDetails) => void
}

// TODO:Update the city dropdown to fetch cities based on selected country

function TDPDetails({ onSaveSuccess, setTenantDetails }: TDPDetailsProps) {
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onBlur', // Show errors on blur for better UX
    defaultValues: {
      companyName: '',
      companyLegalName: '',
      registrationNumber: '',
      contactPersonFirstName: '',
      contactPersonLastName: '',
      email: '',
      phoneNumber: '',
      promoteEsim: ConfirmOptions[1],
      countryCode: null,
      country: { label: 'India', value: 'india' },
      city: { label: 'Delhi', value: 'delhi' },
      status: TDPRegistrationStatus[0],
      rejectReason: '',
      internalRemark: '',
      registrationCert: null,
      taxCert: null,
      signatoryDoc: null,
      companyLogo: null,
      refSource: ReferralSource[2],
      monthlyVolume: '',
      currencyCode: CurrencySupported[0],
      businessType: BusinessTypes[1],
      businessAddress: '',
      websiteURL: '',
      soldEsimCount: '',
      projectedEsimCount: '',
      expectedToBookRatio: '',
      projectedMonthlyVolume: '',
      accountManager: { label: 'Shreyansh', value: 'shreyansh' },
      communicationLanguage: CommunicationLanguage[0],
      '3ptp': { label: '', value: '' }
    }
  })

  const faberToken = OrbitSession.get('faberToken')
  const userEmail = OrbitSession.get('email')

  const countryWatch = watch('country')
  const [debouncedTechTerm, setDebouncedTechTerm] = useState('')
  const [techInput, setTechInput] = useState('')
  const debouncedSetTechTerm = useMemo(
    () => debounce((term: string) => setDebouncedTechTerm(term), 500),
    []
  )

  useEffect(() => {
    debouncedSetTechTerm(techInput.trim())
    return () => debouncedSetTechTerm.cancel()
  }, [techInput, debouncedSetTechTerm])

  const { mutate: saveTDPDetails, isLoading: isSavingTdp } = useMutation<
    ITDPResponse,
    Error,
    ITDPBackendData
  >((data: ITDPBackendData) => B2bTDPAPI.saveTDPDetails(data, faberToken), {
    onSuccess: (response, variables) => {
      toast.success('TDP Details saved successfully!')

      const tenantId = response?.data.data || ''

      // Set the tenant details so that it can be used in other tabs of TDP reg form
      setTenantDetails({
        tenantId: tenantId,
        name: variables.company_name, // Use the data that was sent to backend
        currency: variables.tenant_currency || ''
      })

      onSaveSuccess('#catalogDiscount')
    },
    onError: (error) => {
      console.error('API Error:', error)
      toast.error('Failed to create catalog')
    }
  })

  // Fetch countries using React Query
  const {
    data: countriesData,
    // isLoading: countriesLoading,
    error: countriesError
  } = useQuery(
    ['countries'], // query key
    async () => {
      const token = OrbitSession.get('faberToken')
      // Replace with your actual country API call
      const { data } = await B2bCommonApi.getCountryList(token)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transformedCountries = data.map((country: any) => ({
        label: country.countryName, // Changed from country.name
        value: country.countryCode // Changed from country.code
      }))

      return transformedCountries
    },
    {
      staleTime: 1000 * 60 * 30, // 30 minutes - countries don't change often
      cacheTime: 1000 * 60 * 60, // 1 hour
      retry: 3,
      onError: (error) => {
        console.error('Failed to fetch countries:', error)
      }
    }
  )

  const { data: techCompanyOptions = [], isLoading: techCompanyLoading } =
    useQuery(
      ['techCompanies', debouncedTechTerm],
      async () => {
        const { data } = await TechnologyCompanyApi.getTechnologyCompanyDetails(
          faberToken,
          debouncedTechTerm
        )
        const companies = Array.isArray(data) ? data : [data]
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return companies.map((c: any) => ({
          label: c.technology_company_name,
          value: c.id
        }))
      },
      {
        enabled: !!debouncedTechTerm
      }
    )

  // Fetch city from backend using country code
  const {
    data: cityData,
    // isLoading: cityLoading,
    error: cityError
  } = useQuery(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ['cities', (countryWatch as any)?.value || null], // Type assertion to bypass TypeScript error
    async () => {
      const token = OrbitSession.get('faberToken')
      const { data } = await B2bCommonApi.getCityList(
        token,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (countryWatch as any)?.value
      )
      const filteredAndTransformedCities = data
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((city: any) => ({
          label: city.cityName,
          value: city.cityCode,
          countryCode: city.countryCode,
          id: city.id
        }))
      return filteredAndTransformedCities
    },
    {
      staleTime: 1000 * 60 * 30,
      cacheTime: 1000 * 60 * 60,
      retry: 3,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      enabled: Boolean((countryWatch as any)?.value),
      onError: (error) => {
        console.error('Failed to fetch cities:', error)
      }
    }
  )

  // Transform form data to backend format
  const transformFormData = (data: IFormInput): ITDPBackendData => {
    return {
      company_name: data.companyName || '',
      legal_business_name: data.companyLegalName || '',
      business_registration_number: data.registrationNumber || '',
      business_type: data.businessType?.value || '',
      country: data.country?.value || '',
      city: data.city?.value || '',
      registered_business_address: data.businessAddress || '',
      website_url: data.websiteURL || '',
      tenant_currency: data.currencyCode?.value || '',
      status: data.status?.value.toUpperCase() || '',
      is_active: true,
      communication_lang: data.communicationLanguage?.value || '',

      created_by: userEmail,
      updated_by: userEmail,

      TenantDetails: {
        contact_person_first_name: data.contactPersonFirstName || '',
        contact_person_last_name: data.contactPersonLastName || '',
        contact_person_email: data.email || '',
        contact_person_phone_code: data.countryCode?.value || '', // adjust if your FE splits phone code separately
        contract_person_phone_number: data.phoneNumber || '',

        promoting_esim: data.promoteEsim?.value === 'yes',

        sold_esim_count: Number(data.soldEsimCount || 0),
        projected_esim_sales_count: Number(data.projectedEsimCount || 0),
        expected_book_ratio: Number(data.expectedToBookRatio || 0),

        approximate_monthly_volume: data.monthlyVolume || '',
        approximate_project_monthly_volume: data.projectedMonthlyVolume || '',

        prefered_currency_transactions: '', // not in your form, default empty
        company_logo: '',
        referral_source: data.refSource?.value || '',
        technology_company: data['3ptp']?.value || '',
        // TODO:Remove these fields from the FE payload after the be changes
        business_registration_certificate: '',
        tax_identification_certificate: '',
        authorized_signatory: '',

        rebate_period: 'monthly', // default / static until you expose in FE
        assign_product: 'esim', // default / static until you expose in FE

        account_manager: data.accountManager?.value || '',
        internal_remarks: data.internalRemark || '',

        approver: '',
        approval_on: null,
        approver_remarks: data.rejectReason || ''
      }
    }
  }

  const onSubmit: SubmitHandler<IFormInput> = (data) => {
    const backendData = transformFormData(data)
    saveTDPDetails(backendData)
  }
  const isPromotingESim = watch('promoteEsim')
  const is3ptpRefSource = watch('refSource')
  const isStatusRejected = watch('status')

  const validationRules = {
    required: { required: 'This field is required' },
    email: {
      required: 'Email is required',
      pattern: {
        value: /^\S+@\S+$/i,
        message: 'Invalid email format'
      }
    },
    url: {
      pattern: {
        value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
        message: 'Invalid URL format'
      }
    }
  }

  // Prepare countries options with fallback
  const countryOptions = useMemo(() => {
    if (countriesError) {
      console.error('Countries fetch error:', countriesError)
      return []
    }
    return countriesData || []
  }, [countriesData, countriesError])

  // Prepare city options with fallback
  const cityOptions = useMemo(() => {
    if (cityError) {
      console.error('Cities fetch error:', cityError)
      return []
    }
    return cityData || []
  }, [cityData, cityError])

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Company Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h6'
            sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
          >
            Company Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Company Name*
                </Typography>
                <Controller
                  name='companyName'
                  control={control}
                  rules={validationRules.required}
                  render={({ field }) => (
                    <TextField
                      id='companyName'
                      variant='outlined'
                      size='small'
                      fullWidth
                      {...field}
                      error={!!errors.companyName}
                      helperText={errors.companyName?.message}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Legal Business Name (if different)
                </Typography>
                <Controller
                  name='companyLegalName'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id='companyLegalName'
                      variant='outlined'
                      size='small'
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Business Registration Number/Tax ID
                </Typography>
                <Controller
                  name='registrationNumber'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id='registrationNumber'
                      variant='outlined'
                      size='small'
                      fullWidth
                      {...field}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Contact Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h6'
            sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
          >
            Contact Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Contact Person First Name*
                </Typography>
                <Controller
                  name='contactPersonFirstName'
                  control={control}
                  rules={validationRules.required}
                  render={({ field }) => (
                    <TextField
                      id='contactPersonName'
                      variant='outlined'
                      size='small'
                      fullWidth
                      {...field}
                      error={!!errors.contactPersonFirstName}
                      helperText={errors.contactPersonFirstName?.message}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Contact Person Last Name*
                </Typography>
                <Controller
                  name='contactPersonLastName'
                  control={control}
                  rules={validationRules.required}
                  render={({ field }) => (
                    <TextField
                      id='contactPersonName'
                      variant='outlined'
                      size='small'
                      fullWidth
                      {...field}
                      error={!!errors.contactPersonLastName}
                      helperText={errors.contactPersonLastName?.message}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Contact Person Email* (used as login ID)
                </Typography>
                <Controller
                  name='email'
                  control={control}
                  rules={validationRules.email}
                  render={({ field }) => (
                    <TextField
                      id='email'
                      variant='outlined'
                      size='small'
                      fullWidth
                      {...field}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Contact Person Phone Number*
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Controller
                    name='countryCode'
                    control={control}
                    rules={validationRules.required}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        disablePortal
                        options={countryCodes}
                        onChange={(_, data) => field.onChange(data)}
                        size='small'
                        sx={{ minWidth: 120 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.countryCode}
                            helperText={errors.countryCode?.message}
                          />
                        )}
                      />
                    )}
                  />
                  <Controller
                    name='phoneNumber'
                    control={control}
                    rules={validationRules.required}
                    render={({ field }) => (
                      <TextField
                        id='phoneNumber'
                        variant='outlined'
                        size='small'
                        sx={{ flex: 1 }}
                        {...field}
                        error={!!errors.phoneNumber}
                        helperText={errors.phoneNumber?.message}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Location Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h6'
            sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
          >
            Location Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Country*
                </Typography>
                <Controller
                  name='country'
                  control={control}
                  rules={validationRules.required}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      options={countryOptions}
                      disableClearable
                      onChange={(_, data) => field.onChange(data)}
                      size='small'
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          {...field}
                          error={!!errors.country}
                          helperText={errors.country?.message}
                        />
                      )}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>City*</Typography>
                <Controller
                  name='city'
                  control={control}
                  rules={{ required: { value: true, message: 'Required' } }}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      disableClearable
                      options={cityOptions}
                      onChange={(_, data) => field.onChange(data)}
                      size='small'
                      fullWidth
                      renderInput={(params) => (
                        <TextField {...params} {...field} />
                      )}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={8} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Registered Business Address*
                </Typography>
                <Controller
                  name='businessAddress'
                  control={control}
                  rules={validationRules.required}
                  render={({ field }) => (
                    <TextField
                      id='businessAddress'
                      variant='outlined'
                      size='small'
                      fullWidth
                      multiline
                      rows={2}
                      {...field}
                      error={!!errors.businessAddress}
                      helperText={errors.businessAddress?.message}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Business Details Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h6'
            sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
          >
            Business Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Website URL
                </Typography>
                <Controller
                  name='websiteURL'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id='websiteURL'
                      variant='outlined'
                      size='small'
                      fullWidth
                      placeholder='https://example.com'
                      {...field}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Preferred Currency for transactions*
                </Typography>
                <Controller
                  name='currencyCode'
                  control={control}
                  rules={{ required: { value: true, message: 'Required' } }}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      disablePortal
                      disableClearable
                      options={CurrencySupported}
                      size='small'
                      fullWidth
                      onChange={(_, data) => field.onChange(data)}
                      renderInput={(params) => {
                        return <TextField {...params} />
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Business Type*
                </Typography>
                <Controller
                  name='businessType'
                  control={control}
                  rules={{ required: { value: true, message: 'Required' } }}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      disableClearable
                      disablePortal
                      options={BusinessTypes}
                      size='small'
                      fullWidth
                      onChange={(_, data) => field.onChange(data)}
                      renderInput={(params) => {
                        return <TextField {...params} />
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* eSIM Promotion Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h6'
            sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
          >
            eSIM Promotion
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ mb: 1, fontWeight: 500 }}>
              Currently Promoting eSIM
            </Typography>
            <Controller
              name='promoteEsim'
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  disablePortal
                  disableClearable
                  options={ConfirmOptions}
                  size='small'
                  sx={{ width: 200 }}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => {
                    return <TextField {...params} />
                  }}
                />
              )}
            />
          </Box>

          {isPromotingESim.value == 'yes' && (
            <Box
              sx={{
                borderRadius: 2,
                mt: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: 3,
                color: 'white'
              }}
            >
              <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
                eSIM Sales Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <Box>
                    <Typography sx={{ mb: 1, fontWeight: 500, color: 'white' }}>
                      Monthly Volume of eSIM Sales
                    </Typography>
                    <Box
                      sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                    >
                      <Controller
                        name='currencyCode'
                        control={control}
                        render={({ field }) => (
                          <TextField
                            variant='outlined'
                            size='small'
                            disabled={true}
                            sx={{
                              width: 80,
                              '& .MuiInputBase-root': {
                                backgroundColor: 'rgba(255,255,255,0.9)'
                              }
                            }}
                            value={field.value?.label || ''}
                          />
                        )}
                      />
                      <Controller
                        name='monthlyVolume'
                        control={control}
                        render={({ field }) => (
                          <TextField
                            variant='outlined'
                            size='small'
                            sx={{
                              flex: 1,
                              '& .MuiInputBase-root': {
                                backgroundColor: 'rgba(255,255,255,0.9)'
                              }
                            }}
                            {...field}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item md={6} xs={12}>
                  <Box>
                    <Typography sx={{ mb: 1, fontWeight: 500, color: 'white' }}>
                      Sold eSIM Count (last 12 months)
                    </Typography>
                    <Controller
                      name='soldEsimCount'
                      control={control}
                      render={({ field }) => (
                        <TextField
                          variant='outlined'
                          size='small'
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': {
                              backgroundColor: 'rgba(255,255,255,0.9)'
                            }
                          }}
                          {...field}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                <Grid item md={6} xs={12}>
                  <Box>
                    <Typography sx={{ mb: 1, fontWeight: 500, color: 'white' }}>
                      Projected eSIM Sales Count in next 2 years
                    </Typography>
                    <Controller
                      name='projectedEsimCount'
                      control={control}
                      render={({ field }) => (
                        <TextField
                          variant='outlined'
                          size='small'
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': {
                              backgroundColor: 'rgba(255,255,255,0.9)'
                            }
                          }}
                          {...field}
                        />
                      )}
                    />
                  </Box>
                </Grid>

                <Grid item md={6} xs={12}>
                  <Box>
                    <Typography sx={{ mb: 1, fontWeight: 500, color: 'white' }}>
                      Projected monthly eSIM Sales Volume
                    </Typography>
                    <Box
                      sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}
                    >
                      <Controller
                        name='currencyCode'
                        control={control}
                        render={({ field }) => (
                          <TextField
                            variant='outlined'
                            size='small'
                            disabled={true}
                            sx={{
                              width: 80,
                              '& .MuiInputBase-root': {
                                backgroundColor: 'rgba(255,255,255,0.9)'
                              }
                            }}
                            value={field.value?.label || ''}
                          />
                        )}
                      />
                      <Controller
                        name='projectedMonthlyVolume'
                        control={control}
                        render={({ field }) => (
                          <TextField
                            variant='outlined'
                            size='small'
                            sx={{
                              flex: 1,
                              '& .MuiInputBase-root': {
                                backgroundColor: 'rgba(255,255,255,0.9)'
                              }
                            }}
                            {...field}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Typography sx={{ mb: 1, fontWeight: 500, color: 'white' }}>
                      Expected Look to Book Ratio
                    </Typography>
                    <Controller
                      name='expectedToBookRatio'
                      control={control}
                      render={({ field }) => (
                        <TextField
                          variant='outlined'
                          size='small'
                          sx={{
                            width: 200,
                            '& .MuiInputBase-root': {
                              backgroundColor: 'rgba(255,255,255,0.9)'
                            }
                          }}
                          {...field}
                        />
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>

        {/* Additional Information Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h6'
            sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
          >
            Additional Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item md={6} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Communication Language*
                </Typography>
                <Box>
                  <Controller
                    name='communicationLanguage'
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        {...field}
                        disablePortal
                        disableClearable
                        options={CommunicationLanguage}
                        size='small'
                        fullWidth
                        onChange={(_, data) => field.onChange(data)}
                        renderInput={(params) => {
                          return <TextField {...params} />
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item md={6} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Referral Source
                </Typography>
                <Controller
                  name='refSource'
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      disablePortal
                      disableClearable
                      options={ReferralSource}
                      size='small'
                      fullWidth
                      onChange={(_, data) => field.onChange(data)}
                      renderInput={(params) => {
                        return <TextField {...params} />
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>

          {is3ptpRefSource.value == '3ptp' && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ mb: 1, fontWeight: 500 }}>
                Technology Company*
              </Typography>
              <Controller
                name='3ptp'
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    disablePortal
                    options={techCompanyOptions}
                    value={field.value}
                    onChange={(_, data) => field.onChange(data)}
                    inputValue={techInput}
                    onInputChange={(_, v) => setTechInput(v)}
                    loading={techCompanyLoading}
                    getOptionLabel={(opt) => opt?.label ?? ''}
                    isOptionEqualToValue={(opt, v) =>
                      String(opt?.value) === String(v?.value)
                    }
                    size='small'
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder='Search technology company'
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: <>{params.InputProps.endAdornment}</>
                        }}
                      />
                    )}
                  />
                )}
              />
            </Box>
          )}
        </Box>
        {/* Administrative Section */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant='h6'
            sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
          >
            Administrative
          </Typography>
          <Grid container spacing={3}>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Account Manager*
                </Typography>
                <Controller
                  name='accountManager'
                  control={control}
                  rules={{ required: { value: true, message: 'Required' } }}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      disablePortal
                      disableClearable
                      options={[]}
                      size='small'
                      fullWidth
                      onChange={(_, data) => field.onChange(data)}
                      renderInput={(params) => {
                        return <TextField {...params} />
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>Status*</Typography>
                <Controller
                  name='status'
                  control={control}
                  rules={{ required: { value: true, message: 'Required' } }}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      disablePortal
                      disableClearable
                      options={TDPRegistrationStatus}
                      size='small'
                      fullWidth
                      onChange={(_, data) => field.onChange(data)}
                      renderInput={(params) => {
                        return <TextField {...params} />
                      }}
                    />
                  )}
                />
              </Box>
            </Grid>
            <Grid item md={4} xs={12}>
              <Box>
                <Typography sx={{ mb: 1, fontWeight: 500 }}>
                  Internal Remarks
                </Typography>
                <Controller
                  name='internalRemark'
                  control={control}
                  render={({ field }) => (
                    <TextField
                      id='internalRemark'
                      variant='outlined'
                      size='small'
                      fullWidth
                      multiline
                      rows={2}
                      {...field}
                    />
                  )}
                />
              </Box>
            </Grid>
          </Grid>

          {isStatusRejected.value == 'rejected' && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ mb: 1, fontWeight: 500 }}>Reason*</Typography>
              <Controller
                name='rejectReason'
                control={control}
                rules={{ required: { value: true, message: 'Required' } }}
                render={({ field }) => (
                  <TextField
                    id='rejectReason'
                    variant='outlined'
                    size='small'
                    fullWidth
                    multiline
                    rows={2}
                    {...field}
                  />
                )}
              />
            </Box>
          )}
        </Box>

        {/* Form Footer */}
        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            pt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            variant='body2'
            sx={{
              fontStyle: 'italic',
              color: 'text.secondary'
            }}
          >
            * Required Fields
          </Typography>
          <Button
            type='submit'
            variant='contained'
            disableElevation
            size='large'
            disabled={isSavingTdp}
            sx={{
              minWidth: 120,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600
            }}
          >
            {isSavingTdp ? <CircularProgress /> : 'Save Details'}
          </Button>
        </Box>
      </form>
    </Box>
  )
}

export default TDPDetails
